import { AchievementsPage } from '@/components/Achievements/AchievementsPage';
import { measure } from '@/components/Analytics/Analytics';
import Calendar from '@/components/Calendar/Calendar';
import Collections from '@/components/Collections/Collections';
import SearchIcon from '@/components/Icon/SearchIcon';
import IconButton from '@/components/IconButton/IconButton';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import Search, { openSearchIfBarIsVisible } from '@/components/Search/Search';
import SeasonCountdown from '@/components/SeasonCountdown/SeasonCountdown';
import SeasonIllustrationBackground from '@/components/SeasonIllustrationBackground/SeasonIllustrationBackground';
import { LargeSectionBackground } from '@/components/SectionBackground/SectionBackground';
import Tabs from '@/components/Tabs/Tabs';
import { tabValues, useActiveTab } from '@/components/Tabs/useActiveTab';
import WordMarkLogo from '@/components/WordMarkLogo/WordMarkLogo';
import RememberScrollPositionContext from '@/context/RememberScrollPositionContext';
import useIsNative, {
	useCurrentNativeVersion,
	useNativePlatform,
} from '@/hooks/native/useIsNative';
import useSpecies from '@/hooks/species/useSpecies';
import cn from '@/lib/cs';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const getConditionalInertAttribute = (isInteractive: boolean) =>
	isInteractive
		? {}
		: {
				inert: 'true',
				'aria-hidden': true,
			};

const IndexRoute = ({ isVisible = true }: { isVisible?: boolean }) => {
	const activeTab = useActiveTab();

	const conditionalInertAttribute = getConditionalInertAttribute(isVisible);

	return (
		<div
			className={isVisible ? '' : '-translate-x-full opacity-0'}
			{...conditionalInertAttribute}>
			<SeasonIllustrationBackground>
				<div className="absolute inset-0 size-full landscape:flex">
					<Tabs isVisible={isVisible} />
					<div className="relative size-full grow">
						<div className="absolute inset-x-0">
							<Search
								alwaysShowSearchBar
								searchResultTarget="details"
							/>
						</div>
						<TabContainer
							id="calendar"
							isActive={isVisible && activeTab === 'calendar'}>
							<Calendar />
						</TabContainer>
						<TabContainer
							id="collections"
							isActive={isVisible && activeTab === 'collections'}>
							<Collections />
						</TabContainer>
						<TabContainer
							id="achievements"
							isActive={
								isVisible && activeTab === 'achievements'
							}>
							<AchievementsPage />
						</TabContainer>
					</div>
				</div>
			</SeasonIllustrationBackground>
		</div>
	);
};

const TabContainer = ({
	id,
	isActive,
	children,
}: {
	id: (typeof tabValues)[number];
	isActive: boolean;
	children: React.ReactNode;
}) => {
	const navigate = useNavigate();

	const isNative = useIsNative();
	const nativePlatform = useNativePlatform();
	const currentNativeVersion = useCurrentNativeVersion();

	const tabId = `tab-${id}`;
	const rememberScrollPosition = useContext(RememberScrollPositionContext);

	const scrollRef = useRef<HTMLDivElement>(null);

	const conditionalInertAttribute = getConditionalInertAttribute(isActive);

	const { species } = useSpecies();

	const [shouldRender, setShouldRender] = useState(isActive);

	useEffect(() => {
		// Set shouldRender to true when tab is active
		if (isActive) {
			setShouldRender(true);
		}

		// Restore scroll position
		const scrollPositionInMemory = rememberScrollPosition.get(tabId);
		if (
			isActive &&
			scrollRef.current &&
			scrollPositionInMemory &&
			scrollRef.current.scrollTop === 0
		) {
			scrollRef.current.scrollTo(0, scrollPositionInMemory);
		}
	}, [isActive, scrollRef, rememberScrollPosition, tabId]);

	// Open hidden options link after clicking logo 6 times
	const [
		logoHiddenOptionsLinkClickedTimes,
		setLogoHiddenOptionsLinkClickedTimes,
	] = useState(0);
	const clickOnHiddenOptionsLink = useCallback(() => {
		if (logoHiddenOptionsLinkClickedTimes < 5) {
			setLogoHiddenOptionsLinkClickedTimes((prev) => prev + 1);
			return;
		}
		navigate('/opties');
		setLogoHiddenOptionsLinkClickedTimes(0);
	}, [logoHiddenOptionsLinkClickedTimes, navigate]);

	return (
		<div
			ref={scrollRef}
			className={cn(
				'relative grow size-full overflow-y-auto overflow-x-hidden',
				isActive ? '' : 'absolute -left-full -z-50',
			)}
			onScroll={(event) => {
				rememberScrollPosition.set(
					tabId,
					event.currentTarget.scrollTop,
				);
			}}
			{...conditionalInertAttribute}>
			<div className="flex items-center justify-start p-4 pt-24">
				<h1 className="mx-auto" onClick={clickOnHiddenOptionsLink}>
					<span className="sr-only">SoortSafari</span>
					<WordMarkLogo className="h-32 xs:h-44" withShadow />
				</h1>
			</div>

			{shouldRender && children}

			{!!species && (
				<p className="relative mx-auto mt-16 max-w-xs text-balance p-4 pt-6 text-center text-primary">
					<LabelBackground className="text-white" />
					<span className="relative">
						Kan je een soort niet vinden? Probeer de zoekfunctie!
					</span>
					<IconButton
						className="mx-auto block pt-2"
						icon={<SearchIcon />}
						aria-label="Zoeken"
						title="Zoeken"
						onClick={() => {
							openSearchIfBarIsVisible();
							measure('search-open-secondary', 'navigation');
						}}
					/>
				</p>
			)}

			<footer
				aria-label="Informatie"
				className="relative mt-12 text-white">
				<LargeSectionBackground className="text-primary" />
				<SeasonCountdown />
				<p className="relative mx-auto w-full max-w-sm text-balance px-4 pb-32 pt-0">
					Klokhuis SoortSafari is een project van Het Klokhuis (NTR).
					De fotoherkenning wordt geleverd door Naturalis Biodiversity
					Center en Waarneming.nl. Meer informatie over dit project is
					te vinden op de website van Het Klokhuis. De
					wetenschappelijke tekeningen zijn afkomstig van Naturalis /
					Erik-Jan Bosch.
					<span className="block h-6" />
					SoortSafari verzamelt anonieme gebruiksstatistieken om de
					app te verbeteren. Voor het verzamelen van deze gegevens
					worden mogelijk cookies gebruikt. Jouw foto's en geluiden
					worden tijdens de herkenning tijdelijk opgeslagen. Daarna
					staan jouw foto's en geluiden alleen opgeslagen op jouw
					apparaat.
					<span className="block h-6" />
					Je gebruikt op dit moment versie:{' '}
					{isNative
						? `${nativePlatform}-${currentNativeVersion}`
						: 'web'}
					.
				</p>
			</footer>
		</div>
	);
};

export default IndexRoute;
